// Composer Studio — 복사/잘라내기 소스 셀 표시 오버레이("marching ants", 엑셀식).
// Ctrl+C/X로 복사한 셀(범위)의 외곽 bounding box에 흐르는 점선 테두리를 그린다.
// 셀 rect 측정 방식은 SelectionOverlay와 동일(canvasRect로 [data-composer-cell] 측정).
// 선택(파랑)과 구분되게 초록(#16a34a) 점선을 쓰고, RenderTemplate 뒤(맨 위) 레이어에 그린다.

import { getCellRect, type Rect } from './canvasRect';
import type { CellRef } from './computeCellRange';

// 선택 셀들의 canvas 상대 rect를 합쳐 외곽 bounding box(min~max)를 만든다.
// 비직사각/병합 선택도 외곽 사각형 하나로(엑셀 동형). 측정 실패 셀은 스킵, 전부 실패면 null.
function boundingBox(canvas: HTMLElement, tableId: string, cells: CellRef[]): Rect | null {
  let minTop = Infinity;
  let minLeft = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;
  for (const c of cells) {
    const r = getCellRect(canvas, tableId, c.row, c.col);
    if (!r) continue;
    minTop = Math.min(minTop, r.top);
    minLeft = Math.min(minLeft, r.left);
    maxRight = Math.max(maxRight, r.left + r.width);
    maxBottom = Math.max(maxBottom, r.top + r.height);
  }
  if (!Number.isFinite(minTop)) return null; // 측정된 셀이 하나도 없음(행/열 삭제 등)
  return { top: minTop, left: minLeft, width: maxRight - minLeft, height: maxBottom - minTop };
}

export function ClipboardSourceOverlay({
  canvas,
  source,
}: {
  canvas: HTMLElement | null;
  source: { tableId: string; cells: CellRef[] } | null;
}) {
  if (!canvas || !source || source.cells.length === 0) return null;
  const box = boundingBox(canvas, source.tableId, source.cells);
  if (!box) return null; // 소스 셀이 사라짐 — 미표시(크래시 금지)
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 37 }}>
      {/* 4변 흐르는 점선(marching ants) — 각 변을 dashed gradient로 깔고 background-position 애니메이션. */}
      <style>{`
        @keyframes composer-marching-ants {
          to {
            background-position: 16px 0, -16px 100%, 0 -16px, 100% 16px;
          }
        }
        .composer-marching-ants {
          background-image:
            linear-gradient(90deg, #16a34a 50%, transparent 50%),
            linear-gradient(90deg, #16a34a 50%, transparent 50%),
            linear-gradient(0deg, #16a34a 50%, transparent 50%),
            linear-gradient(0deg, #16a34a 50%, transparent 50%);
          background-size: 8px 2px, 8px 2px, 2px 8px, 2px 8px;
          background-position: 0 0, 0 100%, 0 0, 100% 0;
          background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
          animation: composer-marching-ants 0.5s infinite linear;
        }
      `}</style>
      <div
        className="composer-marching-ants"
        style={{
          position: 'absolute',
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
